import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  useColorMode,
  Divider,
} from '@chakra-ui/react';
import { NavbarWithCallToAction } from '../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { getColor, brandConfig } from '../brandConfig';

export const PrivacyPolicy = () => {
  const { colorMode } = useColorMode();
  const lastUpdated = "November 19, 2025";

  return (
    <>
      <NavbarWithCallToAction />
      <Box minH="100vh" bg={getColor('background.main', colorMode)} py={20}>
        <Container maxW="900px">
          <VStack spacing={8} align="start">
            <VStack spacing={2} align="start" w="full">
              <Text fontSize="sm" color={getColor('text.secondary', colorMode)}>
                Last updated: {lastUpdated}
              </Text>
              <Heading
                as="h1"
                size="2xl"
                color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
              >
                Privacy Policy
              </Heading>
            </VStack>

            <Divider />

            <VStack spacing={6} align="start" w="full">
              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  1. General Information
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  {brandConfig.siteName} ("we", "us", or "our") operates this website (the "Website").
                  We take the protection of your personal data very seriously. We collect, process, and use
                  your personal data in accordance with this privacy policy and in compliance with applicable
                  data protection regulations.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  2. Information We Collect
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={3}>
                  We may collect several types of information from and about users of our Website, including:
                </Text>
                <VStack spacing={2} align="start" pl={6}>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • Personal information that you provide by filling in forms on our Website
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • Information about your internet connection and equipment
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • Details of your visits to our Website and resources you access
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • Information you provide when registering for events or services
                  </Text>
                </VStack>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  3. How We Use Your Information
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={3}>
                  We use information that we collect about you or that you provide to us, including any personal information:
                </Text>
                <VStack spacing={2} align="start" pl={6}>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • To present our Website and its contents to you
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • To provide you with information or services that you request from us
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • To fulfill any other purpose for which you provide it
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • To notify you about changes to our Website or services
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • For any other purpose with your consent
                  </Text>
                </VStack>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  4. Data Security
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  We use appropriate technical and organizational security measures to protect your stored
                  personal data against manipulation, partial or complete loss, and unauthorized access by
                  third parties. Our security measures are continuously improved in line with technical developments.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  5. Cookies and Tracking
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  Our Website may use cookies to distinguish you from other users. This helps us provide you
                  with a good experience when you browse our Website and allows us to improve our Website.
                  For detailed information about the cookies we use, please see our Cookie Policy.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  6. Your Rights
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={3}>
                  You have the right to:
                </Text>
                <VStack spacing={2} align="start" pl={6}>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • Access your personal data
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • Rectify inaccurate personal data
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • Request erasure of your personal data
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • Object to processing of your personal data
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • Request restriction of processing your personal data
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • Withdraw consent at any time
                  </Text>
                </VStack>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  7. Third-Party Services
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  We may employ third-party companies and individuals to facilitate our Website, provide
                  services on our behalf, or assist us in analyzing how our Website is used. These third
                  parties have access to your personal data only to perform these tasks on our behalf and
                  are obligated not to disclose or use it for any other purpose.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  8. Changes to This Privacy Policy
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  We may update our Privacy Policy from time to time. We will notify you of any changes by
                  posting the new Privacy Policy on this page and updating the "Last updated" date. You are
                  advised to review this Privacy Policy periodically for any changes.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  9. Contact Us
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  If you have any questions about this Privacy Policy, please contact us at{' '}
                  {brandConfig.contact.email && (
                    <Text as="span" color={getColor('accent.primary', colorMode)} fontWeight="medium">
                      {brandConfig.contact.email}
                    </Text>
                  )}
                </Text>
              </Box>
            </VStack>
          </VStack>
        </Container>
      </Box>
      <FooterWithFourColumns />
    </>
  );
};

export default PrivacyPolicy;
